/**
 * Annotated Image version grouping — the single definition of "which
 * `annotatedImage` body blocks form one version switcher".
 *
 * An `annotatedImage` can carry an optional `label` (localeString, e.g.
 * "Black Lace" / "Zentai") and an optional `originalImage` (the un-annotated
 * photo behind the Hide/Show annotations toggle). Consecutive blocks that are
 * ALL labelled and share the SAME `originalImage` asset (both absent counts as
 * equal) render as one block with a version switcher. A label on any other
 * block is never displayed — it is never used as a caption (CAS migration
 * plan, owner decision O1).
 *
 * Used by the Studio "lone version label" warning on the tutorial and CAS
 * `body` fields (schemas/documents/tutorial.ts + cas.ts) AND by the public
 * renderer (`groupVersionRuns` in portable-text-renderer.tsx), so what Studio
 * warns about is exactly what the page draws.
 *
 * Plain `.ts` (no React, no Sanity imports) so both server code and Studio
 * schema code can import it — see memory note rsc-client-module-fn-import-trap.
 */

type UnknownRecord = Record<string, unknown>

function isRecord(v: unknown): v is UnknownRecord {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** True when a localeString `label` has at least one non-blank locale value. */
export function hasVersionLabel(label: unknown): boolean {
  if (typeof label === 'string') return label.trim().length > 0
  if (!isRecord(label)) return false
  return Object.entries(label).some(
    ([k, v]) => !k.startsWith('_') && typeof v === 'string' && v.trim().length > 0,
  )
}

/**
 * The `originalImage` asset `_ref` of a block, or null when there is no
 * original photo. Grouping compares by asset, not by crop/hotspot.
 */
export function originalAssetRef(block: unknown): string | null {
  if (!isRecord(block)) return null
  const original = block.originalImage
  if (!isRecord(original)) return null
  const asset = original.asset
  if (!isRecord(asset)) return null
  return typeof asset._ref === 'string' && asset._ref ? asset._ref : null
}

function isLabelledAnnotatedImage(block: unknown): block is UnknownRecord {
  return isRecord(block) && block._type === 'annotatedImage' && hasVersionLabel(block.label)
}

/** Whether two ADJACENT blocks belong to the same version group. */
export function sameVersionGroup(a: unknown, b: unknown): boolean {
  return (
    isLabelledAnnotatedImage(a) &&
    isLabelledAnnotatedImage(b) &&
    originalAssetRef(a) === originalAssetRef(b)
  )
}

/**
 * Split `blocks` into render runs: each run is either a single block, or 2+
 * consecutive blocks that form one version group (rendered as ONE annotated
 * image with a version switcher). Order is preserved; every input block lands
 * in exactly one run. Generic so the renderer can run it over its own render
 * items (`toBlock` maps an item to the block it stands for, or null for an item
 * that can never group, e.g. a list run).
 */
export function groupVersionRuns<T>(
  items: readonly T[],
  toBlock: (item: T) => unknown = (item) => item,
): T[][] {
  const runs: T[][] = []
  for (let i = 0; i < items.length; i++) {
    const prev = runs[runs.length - 1]
    if (prev && i > 0 && sameVersionGroup(toBlock(items[i - 1]), toBlock(items[i]))) {
      prev.push(items[i])
    } else {
      runs.push([items[i]])
    }
  }
  return runs
}

/**
 * Indices of labelled `annotatedImage` blocks in `blocks` whose label will
 * NOT be shown, because neither neighbour is in the same version group.
 */
export function findLoneVersionLabels(blocks: readonly unknown[]): number[] {
  const lone: number[] = []
  for (let i = 0; i < blocks.length; i++) {
    if (!isLabelledAnnotatedImage(blocks[i])) continue
    const grouped =
      (i > 0 && sameVersionGroup(blocks[i - 1], blocks[i])) ||
      (i < blocks.length - 1 && sameVersionGroup(blocks[i], blocks[i + 1]))
    if (!grouped) lone.push(i)
  }
  return lone
}

export interface LoneVersionLabel {
  /** Locale array the block sits in (`en`, `ja`, …). */
  locale: string
  /** Set when the block is inside a chapter: that chapter's locale array. */
  chapterKey?: string
  chapterLocale?: string
  /** The block's `_key`, or `#<index>` when it has none. */
  blockKey: string
}

function localeArrays(value: unknown): Array<[string, unknown[]]> {
  if (!isRecord(value)) return []
  return Object.entries(value).filter(
    (e): e is [string, unknown[]] => !e[0].startsWith('_') && Array.isArray(e[1]),
  )
}

function blockKeyOf(blocks: readonly unknown[], i: number): string {
  const b = blocks[i]
  return isRecord(b) && typeof b._key === 'string' ? b._key : `#${i}`
}

/**
 * Walk a `localePortableText` body (top level of every locale array, plus
 * the content arrays of every top-level chapter) and list each lone version
 * label. Adjacency is only ever within one array.
 */
export function findLoneVersionLabelsInBody(body: unknown): LoneVersionLabel[] {
  const out: LoneVersionLabel[] = []
  for (const [locale, blocks] of localeArrays(body)) {
    for (const i of findLoneVersionLabels(blocks)) {
      out.push({ locale, blockKey: blockKeyOf(blocks, i) })
    }
    blocks.forEach((block, ci) => {
      if (!isRecord(block) || block._type !== 'chapter') return
      const chapterKey = blockKeyOf(blocks, ci)
      for (const [chapterLocale, inner] of localeArrays(block.content)) {
        for (const i of findLoneVersionLabels(inner)) {
          out.push({ locale, chapterKey, chapterLocale, blockKey: blockKeyOf(inner, i) })
        }
      }
    })
  }
  return out
}

/** Studio warning text for a body, or `true` when there is nothing to flag. */
export function loneVersionLabelWarning(body: unknown): true | string {
  const lone = findLoneVersionLabelsInBody(body)
  if (lone.length === 0) return true
  const where = Array.from(
    new Set(lone.map((l) => (l.chapterKey ? `${l.locale} (chapter)` : l.locale))),
  ).join(', ')
  const n = lone.length
  return (
    `${n} Annotated Image version label${n === 1 ? ' is' : 's are'} not shown (${where}). ` +
    'A version label only appears as a switcher button when 2 or more labelled ' +
    'Annotated Images with the same Original photo sit next to each other.'
  )
}
